import React from 'react';
import { Input, styled, BoxProps, Box } from '@mui/material';
import { grey } from '@mui/material/colors';
import { v4 as uuidv4 } from 'uuid';

import { Button, Typography, SystemUpdateAltIcon } from '@occc-app/design-system';

type DropFieldProps = {
  dragging: boolean;
} & BoxProps;

const DropField = styled(Box, {
  shouldForwardProp: (propName: string) => propName !== 'dragging',
})<DropFieldProps>(({ theme, dragging }) => ({
  backgroundColor: dragging ? grey[200] : grey[100],
  alignItems: 'center',
  border: '2px dashed',
  borderColor: grey[300],
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  padding: theme.spacing(8, 2),
  textAlign: 'center',
}));

const defaultUploadCopy = 'Drag and Drop Files Here';

type Props = {
  disableClick?: boolean;
  draggingStyle?: object;
  label?: string;
  onDrop: (files: File[]) => void;
  onFileDialogCancel?: () => void;
  staticStyle?: object;
  uploadCopy?: string;
};

type InstanceState = {
  dragging: boolean;
  inputId: string;
};

export class DropFileInput extends React.Component<Props, InstanceState> {
  state: InstanceState = {
    dragging: false,
    inputId: '',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.state,
      inputId: uuidv4(),
    };
  }

  componentDidMount() {
    window.addEventListener('dragover', (event: Event) => {
      this.overrideEventDefaults(event);
    });
    window.addEventListener('drop', (event: Event) => {
      this.overrideEventDefaults(event);
    });
  }

  componentWillUnmount() {
    window.removeEventListener('dragover', this.overrideEventDefaults);
    window.removeEventListener('drop', this.overrideEventDefaults);
  }

  onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    this.overrideEventDefaults(event);

    const isDraggingIE = event.dataTransfer.types && event.dataTransfer.types[0] === 'Files';
    const isDragging = (event.dataTransfer.items && event.dataTransfer.items[0]) || isDraggingIE;
    if (isDragging) {
      this.setState({ dragging: true });
    }
  };

  onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    this.overrideEventDefaults(event);

    this.setState({ dragging: false });
  };

  onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    this.overrideEventDefaults(event);

    event.dataTransfer.dropEffect = 'copy';
  };

  onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    this.overrideEventDefaults(event);

    this.setState({ dragging: false });

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      this.props.onDrop(Array.from(files));
    }
  };

  onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onDrop, onFileDialogCancel } = this.props;

    const files = event.target.files;
    if (files && files.length > 0) {
      onDrop(Array.from(files));
    } else if (onFileDialogCancel) {
      onFileDialogCancel();
    }
  };

  overrideEventDefaults = (event: Event | React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  renderLabel() {
    const { label } = this.props;

    if (!label) {
      return null;
    }

    return <Typography paragraph>{label}</Typography>;
  }

  render() {
    const { uploadCopy } = this.props;

    const { dragging, inputId } = this.state;

    return (
      <Box>
        <Box mb={4}>
          {this.renderLabel()}
          <label htmlFor={inputId}>
            <DropField
              dragging={dragging}
              onDragEnter={this.onDragEnter}
              onDragLeave={this.onDragLeave}
              onDragOver={this.onDragOver}
              onDrop={this.onDrop}
            >
              <Box
                bgcolor='common.white'
                borderRadius='100%'
                display='flex'
                alignItems='center'
                border='1px solid'
                borderColor={grey[300]}
                p={2}
                mb={4}
              >
                <SystemUpdateAltIcon fontSize='large' />
              </Box>

              <Typography variant='h6' component='p'>
                {uploadCopy || defaultUploadCopy}
              </Typography>
              <Box my={3}>
                <Typography>OR</Typography>
              </Box>
              <Button variant='contained' color='secondary'>
                <Input
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    opacity: '0',
                  }}
                  inputProps={{
                    multiple: false,
                    accept: '.csv',
                  }}
                  onChange={this.onFileInputChange}
                  type='file'
                />
                Browse Files
              </Button>

              <Box mt={5}>
                <Typography variant='body1' color='textSecondary'>
                  Maximum allowed file size is 10 MB. It's recommended to upload a maximum of
                  250,000 members at a time.
                </Typography>
              </Box>
            </DropField>
          </label>
        </Box>
      </Box>
    );
  }
}
